// Party read-flip (WS-A) — typeahead picker over the Borrower/Seller party
// registries (the CounterpartyPicker API shape: debounced ≥2-char search,
// keyboard navigable, chip with clear affordance). Needed by deal surfaces
// that select a party by type.

import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, X } from "lucide-react";
import { Input, Label } from "@/design-system/components/ui";
import { partiesApi } from "@/api/parties";
import { partiesKeys } from "@/hooks/parties/partiesKeys";
import type { PartyType } from "@/types/parties";
import { displayEmail } from "@/design-system/lib/format";

export interface SelectedParty {
  id: string;
  name: string;
  detail?: string | null;
}

interface PickerResult {
  id: string;
  name: string;
  email: string | null;
}

const MAX_RESULTS = 20;

function useDebounced(value: string, ms = 250): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

export interface PartyPickerProps {
  type: PartyType;
  value: SelectedParty | null;
  onChange: (value: SelectedParty | null) => void;
  label?: string;
}

export function PartyPicker({ type, value, onChange, label }: PartyPickerProps) {
  const [search, setSearch] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounced = useDebounced(search);
  const term = debounced.trim();
  const enabled = term.length >= 2;
  const fieldLabel = label ?? type;

  const results = useQuery<PickerResult[]>({
    queryKey: partiesKeys.search(type, term),
    queryFn: async () => {
      const page = await partiesApi.list(type, { search: term, limit: MAX_RESULTS });
      return page.data.map((p) => ({
        id: p.id,
        name: p.displayName,
        email: displayEmail(p.email),
      }));
    },
    enabled,
  });
  const options = useMemo(() => results.data ?? [], [results.data]);

  useEffect(() => setActiveIndex(-1), [term, type]);

  const select = (r: PickerResult) => {
    onChange({ id: r.id, name: r.name, detail: r.email });
    setSearch("");
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (options.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % options.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? options.length - 1 : i - 1));
    } else if (e.key === "Enter") {
      if (activeIndex >= 0 && activeIndex < options.length) {
        e.preventDefault();
        select(options[activeIndex]);
      }
    } else if (e.key === "Escape") {
      setSearch("");
    }
  };

  if (value) {
    return (
      <div>
        <Label>{fieldLabel}</Label>
        <span
          className="inline-flex items-center gap-space-2 rounded-full border border-border-light bg-white-warm px-space-2 py-space-1 text-small text-onyx"
          data-testid="party-chip"
        >
          <span className="font-semibold">{value.name}</span>
          {value.detail && <span className="text-caption text-gray-mid">{value.detail}</span>}
          <button
            type="button"
            onClick={() => {
              onChange(null);
              setTimeout(() => inputRef.current?.focus(), 0);
            }}
            aria-label={`Clear selected ${type.toLowerCase()}`}
            className="text-gray-mid hover:text-onyx"
            data-testid="party-chip-clear"
          >
            <X size={12} aria-hidden="true" />
          </button>
        </span>
      </div>
    );
  }

  return (
    <div>
      <Label htmlFor={`party-search-${type}`}>{fieldLabel}</Label>
      <div className="relative">
        <Search
          size={14}
          className="absolute left-space-2 top-1/2 -translate-y-1/2 text-gray-mid"
          aria-hidden="true"
        />
        <Input
          id={`party-search-${type}`}
          ref={inputRef}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={`Search ${type.toLowerCase()} parties by name or email…`}
          className="pl-[28px]"
          role="combobox"
          aria-expanded={enabled && options.length > 0}
          aria-controls={`party-search-listbox-${type}`}
          aria-activedescendant={
            activeIndex >= 0 ? `party-option-${type}-${activeIndex}` : undefined
          }
          autoComplete="off"
          data-testid="party-search-input"
        />
      </div>
      {search.trim().length > 0 && search.trim().length < 2 && (
        <p className="text-caption text-gray-mid mt-1 m-0">Type at least 2 characters to search.</p>
      )}
      {enabled && (
        <ul
          id={`party-search-listbox-${type}`}
          role="listbox"
          aria-label={`${type} search results`}
          className="m-0 mt-space-1 p-0 list-none max-h-56 overflow-y-auto rounded-md border border-border-light bg-white-pure"
          data-testid="party-search-results"
        >
          {results.isLoading && (
            <li className="px-space-2 py-space-1 text-small text-gray-mid italic">Searching…</li>
          )}
          {results.isError && (
            <li className="px-space-2 py-space-1 text-small text-status-error">
              Search failed. Try again.
            </li>
          )}
          {!results.isLoading && !results.isError && options.length === 0 && (
            <li className="px-space-2 py-space-1 text-small text-gray-mid">
              No {type.toLowerCase()} parties match.
            </li>
          )}
          {options.map((r, i) => (
            <li key={r.id} role="presentation">
              <button
                type="button"
                id={`party-option-${type}-${i}`}
                role="option"
                aria-selected={i === activeIndex}
                onClick={() => select(r)}
                onMouseEnter={() => setActiveIndex(i)}
                className={`w-full flex items-baseline gap-space-2 px-space-2 py-space-1 text-left ${
                  i === activeIndex ? "bg-white-warm" : "bg-white-pure"
                }`}
                data-testid={`party-option-${r.id}`}
              >
                <span className="text-small text-onyx font-semibold truncate">{r.name}</span>
                {r.email && (
                  <span className="text-caption text-gray-mid truncate">{r.email}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
